import Loader from '@/components/Loaders/Loader';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import DangerTriangleIcon from '@/images/DangerTriangle.svg';
import Document from '@/images/Document.svg';
import LinkIcon from '@/images/Link.svg';
import TrashBinIcon from '@/images/TrashBin.svg';
import { AgentFileS3UploadStatus, UploadingFile } from '@/state/agent';
import theme from '@/theme';
import { DocumentType } from '@d-id/types';
import styled from '@emotion/styled';
import { useMemo } from 'react';

const UploadedList = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const UploadedItemContainer = styled.div`
    background: ${theme.colors.black[50]};
    display: flex;
    align-items: center;
    justify-content: space-between;

    height: 48px;
    padding: 8px;
    margin: 8px 0;

    border-radius: 8px;
`;

const FileName = styled.div<{ isError?: boolean }>`
    color: ${({ isError, theme }) => (isError ? theme.colors.error.main : theme.colors.black.main)};

    text-align: start;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;

    overflow: auto;
    user-select: text;
`;

const Actions = styled.div<{ disabled?: boolean }>`
    display: flex;
    align-items: center;

    cursor: pointer;

    ${({ disabled }) => disabled && 'opacity: 0.5; pointer-events: none;'}
`;

const iconSize = '16px';

const StyledLoader = styled(Loader)`
    width: ${iconSize};
    height: ${iconSize};
`;

const StartSectionContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(2, auto);
    grid-auto-rows: auto;
    column-gap: 8px;

    & > div {
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }
`;
const FileUploadDetailsContainer = styled.div`
    grid-column-start: 2;
`;

const DetailsText = styled('div')`
    color: ${theme.colors.secondary[400]};
    text-align: center;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
`;

const EndSectionContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;

    gap: 4px;
`;

const EndSectionText = styled.p<{ isError?: boolean }>`
    color: ${({ isError, theme }) => (isError ? theme.colors.error.main : theme.colors.black.main)};
    text-align: center;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 16px;
`;

const useFileStatus = (item: UploadingFile) => {
    const isUploading = useMemo(
        () => [AgentFileS3UploadStatus.Uploading, AgentFileS3UploadStatus.Uploaded].includes(item.status),
        [item.status]
    );
    const isError = useMemo(() => item.status === AgentFileS3UploadStatus.Error, [item.status]);
    const isValidated = useMemo(() => item.status === AgentFileS3UploadStatus.Validated, [item.status]);

    return { isUploading, isError, isValidated };
};

const formatFileDisplayName = (fileName: string, isUploading: boolean) => (isUploading ? 'Uploading $$...' : '$$').replace('$$', fileName);

interface StartSectionProps {
    item: UploadingFile;
}

const StartSection = ({ item }: StartSectionProps) => {
    const { isUploading, isError, isValidated } = useFileStatus(item);
    return (
        <StartSectionContainer>
            <div>
                {isUploading ? (
                    <StyledLoader color="black" />
                ) : (
                    <MaskedIcon
                        size={iconSize}
                        {...(isError ? { color: theme.colors.error.main } : {})}
                        src={isError ? DangerTriangleIcon : item.file.type === DocumentType.Html ? LinkIcon : Document}
                    />
                )}
            </div>
            <div>
                <FileName isError={isError}>{formatFileDisplayName(item.file.name, isUploading)}</FileName>
            </div>
            {(isError || isValidated) && (
                <FileUploadDetailsContainer>
                    <DetailsText>
                        {
                            // FIXME: once we start saving size in the server, we can use this
                            // isValidated
                            //     ? formatFileSize(item.file.size)
                            //     :
                            isError ? (item.file.type === DocumentType.Html ? 'Failed to validate URL' : item.error) : null
                        }
                    </DetailsText>
                </FileUploadDetailsContainer>
            )}
        </StartSectionContainer>
    );
};

interface EndSectionProps extends StartSectionProps {
    handleDeleteFile: (uploadingFile: UploadingFile) => void;
    disableDelete?: boolean;
}

const EndSection = ({ item, handleDeleteFile, disableDelete }: EndSectionProps) => {
    const { isUploading, isError, isValidated } = useFileStatus(item);

    const disabled = isUploading || disableDelete;
    const onDeleteFile = () => {
        if (disabled) return;
        return handleDeleteFile(item);
    };

    if (isUploading) return;
    return (
        <Actions disabled={disabled} onClick={onDeleteFile}>
            {isError ? (
                <EndSectionText isError={isError}>Dismiss</EndSectionText>
            ) : isValidated ? (
                <EndSectionContainer>
                    <MaskedIcon src={TrashBinIcon} size={iconSize} />
                    <EndSectionText>Remove</EndSectionText>
                </EndSectionContainer>
            ) : null}
        </Actions>
    );
};

type UploadedItemProps = EndSectionProps;

const UploadedItem = ({ item, ...props }: UploadedItemProps) => {
    return (
        <UploadedItemContainer>
            <StartSection item={item} />
            <EndSection item={item} {...props} />
        </UploadedItemContainer>
    );
};

interface UploadedListProps extends Omit<UploadedItemProps, 'item'> {
    uploadingFiles?: UploadedItemProps['item'][];
}

export default ({ uploadingFiles = [], ...props }: UploadedListProps) => (
    <UploadedList>
        {uploadingFiles.map(item => (
            <UploadedItem key={item.file.lastModified} {...props} item={item} />
        ))}
    </UploadedList>
);
